import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  ArrowLeft,
  Save,
  RefreshCcw,
  Search,
  Eye,
  Pencil,
  Hash,
  CalendarDays,
  User,
  MapPin,
  Briefcase,
  Wallet,
  IndianRupee,
  Type,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

// dd/MM/yyyy from <input type="date"> yyyy-MM-dd
function toDDMMYYYYFromISO(iso) {
  const s = String(iso || "").trim();
  if (!s) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return "";
  return `${Number(m[3])}/${Number(m[2])}/${Number(m[1])}`.replaceAll(
    "//",
    "/",
  );
}

// dd/MM/yyyy -> ISO yyyy-MM-dd for <input type="date">
function toISOFromDDMMYYYY(ddmmyyyy) {
  const s = String(ddmmyyyy || "").trim();
  if (!s) return "";
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (!m) return "";
  const dd = String(m[1]).padStart(2, "0");
  const mm = String(m[2]).padStart(2, "0");
  const yy = String(m[3]);
  return `${yy}-${mm}-${dd}`;
}

const EmployeeTransferEntry = () => {
  const navigate = useNavigate();
  const { employeeTransferId: employeeTransferIdParam } = useParams();

  const isEdit = !!employeeTransferIdParam;
  const employeeTransferId = isEdit ? Number(employeeTransferIdParam) : 0;

  // optional: support view mode via query ?mode=view or state
  const urlParams = new URLSearchParams(window.location.search);
  const modeParam = String(urlParams.get("mode") || "").toLowerCase();
  const initialViewMode = modeParam === "view";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const fyr = localStorage.getItem("fyr") || ""; // e.g. 2021-2022
  const branchId = Number(localStorage.getItem("branchId") || 0);
  const branchType = localStorage.getItem("branchType") || "HO"; // if you store it; else HO
  const loginuserid = localStorage.getItem("userId") || ""; // if needed for headers
  const screenBadge = useMemo(() => "INFRA SCREEN : C-18/1", []);

  const [loading, setLoading] = useState(false);

  // meta dropdowns
  const [fromSites, setFromSites] = useState([]);
  const [toSites, setToSites] = useState([]);
  const [employeeCodes, setEmployeeCodes] = useState([]);
  const [employeeNames, setEmployeeNames] = useState([]);
  const [employeeCodeText, setEmployeeCodeText] = useState("");
  const [employeeNameText, setEmployeeNameText] = useState("");

  // mode state
  const [viewMode, setViewMode] = useState(initialViewMode);

  // form fields
  const [transferCode, setTransferCode] = useState("");
  const [mode, setMode] = useState("Transfer"); // Transfer / Camp

  const [employeeId, setEmployeeId] = useState("Select");
  const [employeeNameId, setEmployeeNameId] = useState("Select");

  const [designation, setDesignation] = useState("");
  const [department, setDepartment] = useState("");

  const [fromSiteId, setFromSiteId] = useState("Select");
  const [toSiteId, setToSiteId] = useState("Select");

  const [effectDateISO, setEffectDateISO] = useState(""); // input type=date
  const [dailyBata, setDailyBata] = useState("");
  const [monthlySalary, setMonthlySalary] = useState("");

  const [travelAdvance, setTravelAdvance] = useState("");
  const [canteenDue, setCanteenDue] = useState("");
  const [paidUptoISO, setPaidUptoISO] = useState("");

  const [debitTo, setDebitTo] = useState("");
  const [reason, setReason] = useState("");

  // worked period
  const [workedFromISO, setWorkedFromISO] = useState("");
  const [workedToISO, setWorkedToISO] = useState("");
  const [leaveDays, setLeaveDays] = useState("");
  const [lop, setLop] = useState("");

  // ---------------- styling ----------------
  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const disabledMask = viewMode ? "opacity-70 pointer-events-none" : "";

  // ---------------- api helpers ----------------
  const axiosHeaders = () => ({
    "x-loginuserid": loginuserid, // your node uses getLoginUserId from headers
  });

  const fetchMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!branchId) {
      toast.warn("branchId missing in localStorage.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(
        `${BASE_URL}/api/payroll/employee-transfer-entry/meta`,
        {
          params: {
            finYearId: Number(finYearId),
            branchId: Number(branchId),
            branchType: String(branchType || "HO"),
            employeeTransferId: employeeTransferId || 0,
          },
          headers: axiosHeaders(),
          timeout: 60000,
        },
      );

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Meta load failed");
        return;
      }

      setFromSites(res.data.fromSites || []);
      setToSites(res.data.toSites || []);
      setEmployeeCodes(res.data.employeeCodes || []);
      setEmployeeNames(res.data.employeeNames || []);

      // defaults
      setFromSiteId(String(res.data.defaultFromSiteId ?? branchId));
      if (!isEdit) {
        setTransferCode(res.data.transferCode || "");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Meta load failed",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchEdit = async () => {
    if (!isEdit) return;
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(
        `${BASE_URL}/api/payroll/employee-transfer-entry/${employeeTransferId}`,
        {
          params: { finYearId: Number(finYearId) },
          headers: axiosHeaders(),
          timeout: 60000,
        },
      );

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Load failed");
        return;
      }

      const r = res?.data?.row;
      if (!r) {
        toast.warn("Record not found");
        return;
      }

      // Fill fields (ASP binds text, but we prefer IDs for dropdowns)
      setTransferCode(r.transferCode || "");
      setMode(r.mode || "Transfer");

      // Because ASP sets ddl SelectedIndex=1 and text only,
      // we will try to resolve employeeId from employeeCodes list later.
      // Here: keep IDs as Select, but show details via text fields.
      // We'll fetch employee details after meta is loaded if possible.

      setDepartment(r.department || "");
      setDesignation(r.designation || "");

      setEmployeeCodeText(r.empCodeText || r.employeecode || "");
      setEmployeeNameText(r.empNameText || r.employeename || "");

      setEffectDateISO(toISOFromDDMMYYYY(r.effectDate || ""));
      setPaidUptoISO(toISOFromDDMMYYYY(r.paidUpto || ""));

      setDailyBata(String(r.dailyBata ?? ""));
      setMonthlySalary(String(r.monthlySalary ?? ""));
      setTravelAdvance(String(r.travelAdvance ?? ""));
      setCanteenDue(String(r.canteenDue ?? ""));
      setDebitTo(String(r.debitTo ?? ""));
      setReason(String(r.reason ?? ""));

      setWorkedFromISO(toISOFromDDMMYYYY(r.workedFrom || ""));
      setWorkedToISO(toISOFromDDMMYYYY(r.workedTo || ""));
      setLeaveDays(String(r.leaveDays ?? ""));
      setLop(String(r.lop ?? ""));

      // from site: in ASP they set SelectedItem.Text only
      // We'll keep fromSiteId as branchId by default (already).
      // to site: we got id from API
      if (r.toSiteId) setToSiteId(String(r.toSiteId));

      if (r.employeeId) {
        setEmployeeId(String(r.employeeId));
        setEmployeeNameId(String(r.employeeId));
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Load failed",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeDetails = async (empId) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!branchId) {
      toast.warn("branchId missing");
      return;
    }
    if (!empId || String(empId) === "Select") return;

    try {
      setLoading(true);
      const res = await axios.get(
        `${BASE_URL}/api/payroll/employee-transfer-entry/employee-details`,
        {
          params: {
            finYearId: Number(finYearId),
            branchId: Number(branchId),
            employeeId: Number(empId),
          },
          headers: axiosHeaders(),
          timeout: 60000,
        },
      );

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Employee details failed");
        return;
      }

      const row = res?.data?.row;
      if (!row) return;

      // set readonly fields
      setDepartment(row.department || "");
      setDesignation(row.designation || "");
      setDailyBata(String(row.bata ?? ""));
      setMonthlySalary(String(row.salary ?? ""));

      // also sync name dropdown if possible
      const nameMatch = (employeeNames || []).find(
        (x) => String(x.id) === String(empId),
      );
      if (nameMatch) setEmployeeNameId(String(nameMatch.id));
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Employee details failed",
      );
    } finally {
      setLoading(false);
    }
  };

  // ---------------- lifecycle ----------------
  useEffect(() => {
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // after meta, load edit record
  useEffect(() => {
    if (isEdit) fetchEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, employeeTransferId]);

  // when employee codes loaded, try to map edit view text -> id (optional best-effort)
  useEffect(() => {
    // If edit mode, we might not have employeeId set.
    // We can attempt to infer using transfer record employee code text - but API doesn't provide employeeId directly.
    // So we keep manual selection. (Safer)
  }, [employeeCodes]);

  // ---------------- actions ----------------
  const handleBack = () => navigate("/payroll/EmployeeTransferListing");

  const clearForm = () => {
    if (isEdit) {
      toast.info("In Edit mode: Clear will only clear fields (not delete).");
    }
    setMode("Transfer");
    setEmployeeId("Select");
    setEmployeeNameId("Select");
    setDesignation("");
    setDepartment("");
    setFromSiteId(String(branchId || "Select"));
    setToSiteId("Select");
    setEffectDateISO("");
    setDailyBata("");
    setMonthlySalary("");
    setTravelAdvance("");
    setCanteenDue("");
    setPaidUptoISO("");
    setDebitTo("");
    setReason("");
    setWorkedFromISO("");
    setWorkedToISO("");
    setLeaveDays("");
    setLop("");
  };

  const validate = () => {
    if (!transferCode.trim()) {
      toast.warn("Transfer Number missing");
      return false;
    }

    // ✅ only required in ADD mode
    if (!isEdit) {
      if (String(employeeId) === "Select") {
        toast.warn("Select Employee Code");
        return false;
      }
      if (String(employeeNameId) === "Select") {
        toast.warn("Select Employee Name");
        return false;
      }
    }

    if (!fromSiteId || String(fromSiteId) === "Select") {
      toast.warn("Please select From Site");
      return false;
    }
    if (!toSiteId || String(toSiteId) === "Select") {
      toast.warn("Please select To Site");
      return false;
    }
    if (!effectDateISO) {
      toast.warn("Please select Transfer Effective Date");
      return false;
    }
    if (!String(reason || "").trim()) {
      toast.warn("Please Type the Reason");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (viewMode) {
      toast.info("View mode: enable edit to save.");
      return;
    }
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!validate()) return;

    const payload = {
      finYearId: Number(finYearId),
      branchId: Number(branchId),
      transferCode: transferCode.trim(),
      fromSiteId: Number(fromSiteId),
      toSiteId: Number(toSiteId),
      mode,
      effectDate: toDDMMYYYYFromISO(effectDateISO),
      paidUpto: paidUptoISO ? toDDMMYYYYFromISO(paidUptoISO) : "",
      workedFrom: workedFromISO ? toDDMMYYYYFromISO(workedFromISO) : "",
      workedTo: workedToISO ? toDDMMYYYYFromISO(workedToISO) : "",
      monthlySalary: String(monthlySalary || "").trim(),
      travelAdvance: String(travelAdvance || "").trim(),
      dailyBata: String(dailyBata || "").trim(),
      canteenDue: String(canteenDue || "").trim(),
      debitTo: String(debitTo || "").trim(),
      leaveDays: String(leaveDays || "").trim(),
      lop: String(lop || "").trim(),
      reason: String(reason || "").trim(),
      finYearText: String(fyr || "").trim(),
    };

    // ✅ only add employeeId when ADD
    if (!isEdit) payload.employeeId = Number(employeeId);

    try {
      setLoading(true);

      if (isEdit) {
        const res = await axios.put(
          `${BASE_URL}/api/payroll/employee-transfer-entry/update/${employeeTransferId}`,
          payload,
          { headers: axiosHeaders(), timeout: 60000 },
        );

        if (res?.data?.success) {
          toast.success(res?.data?.message || "Updated Successfully..");
          navigate("/payroll/EmployeeTransferListing");
        } else {
          toast.error(res?.data?.message || "Update failed");
        }
      } else {
        const res = await axios.post(
          `${BASE_URL}/api/payroll/employee-transfer-entry/save`,
          payload,
          {
            headers: axiosHeaders(),
            timeout: 60000,
          },
        );

        if (res?.data?.success) {
          toast.success(res?.data?.message || "Transferred Successfully..");
          navigate("/payroll/EmployeeTransferListing");
        } else {
          toast.error(res?.data?.message || "Save failed");
        }
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Submit failed",
      );
    } finally {
      setLoading(false);
    }
  };

  const onEmployeeCodeChange = async (val) => {
    setEmployeeId(val);
    if (val === "Select") {
      clearForm();
      setFromSiteId(String(branchId || "Select"));
      setTransferCode(transferCode); // keep
      return;
    }
    await fetchEmployeeDetails(val);
  };

  const onEmployeeNameChange = async (val) => {
    setEmployeeNameId(val);
    if (val === "Select") return;
    setEmployeeId(val); // ID same
    await fetchEmployeeDetails(val);
  };

  const headerTitle = isEdit
    ? viewMode
      ? "EMPLOYEE TRANSFER VIEW"
      : "EMPLOYEE TRANSFER EDIT"
    : "EMPLOYEE TRANSFER ENTRY";

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">{headerTitle}</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />
        <button
          onClick={handleBack}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Building2
                  className={isDark ? "text-white" : "text-gray-700"}
                />
              </div>

              <div>
                <div
                  className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  Employee Transfer
                </div>
                <div
                  className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}
                >
                  Add / Edit / View (Single Page)
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark
                    ? "bg-blue-900/40 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark
                    ? "bg-emerald-900/40 text-emerald-100"
                    : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {isEdit ? `ID: ${employeeTransferId}` : "New"}
              </div>

              {viewMode && (
                <div
                  className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                    isDark
                      ? "bg-purple-900/40 text-purple-100"
                      : "bg-purple-100 text-purple-800"
                  }`}
                >
                  VIEW MODE
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button
                onClick={fetchMeta}
                className={ghostBtn}
                disabled={loading}
              >
                <RefreshCcw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
                Refresh Meta
              </button>

              {isEdit && (
                <button
                  onClick={() => setViewMode((v) => !v)}
                  className={ghostBtn}
                  title="Toggle View/Edit"
                >
                  {viewMode ? (
                    <>
                      <Pencil className="w-4 h-4" />
                      Enable Edit
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4" />
                      View
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Main Form */}
          <form onSubmit={handleSubmit} className={cardBase + " p-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Transfer Number */}
              <div>
                <label className={labelBase}>
                  Transfer Number <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <Hash
                    className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`}
                  />
                  <input
                    value={transferCode}
                    readOnly
                    className={inputBase + " bg-opacity-60"}
                  />
                </div>
              </div>

              {/* Mode */}
              <div>
                <label className={labelBase}>
                  Mode <span className="text-red-500">*</span>
                </label>
                <div className={`mt-2 flex gap-6 items-center ${disabledMask}`}>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      checked={mode === "Camp"}
                      onChange={() => setMode("Camp")}
                    />
                    Camp
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      checked={mode === "Transfer"}
                      onChange={() => setMode("Transfer")}
                    />
                    Transfer
                  </label>
                </div>
              </div>

              {/* Employee Code */}
              {isEdit ? (
                <div>
                  <label className={labelBase}>
                    Employee Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    className={inputBase}
                    value={employeeCodeText}
                    readOnly
                  />
                </div>
              ) : (
                <div className={disabledMask}>
                  <label className={labelBase}>
                    Employee Code <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={inputBase}
                    value={employeeId}
                    onChange={(e) => onEmployeeCodeChange(e.target.value)}
                    disabled={loading || viewMode}
                  >
                    <option value="Select">Select</option>
                    {(employeeCodes || []).map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.text}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Effective Date */}
              <div className={disabledMask}>
                <label className={labelBase}>
                  Transfer Effective Date{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <CalendarDays
                    className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`}
                  />
                  <input
                    type="date"
                    className={inputBase}
                    value={effectDateISO}
                    onChange={(e) => setEffectDateISO(e.target.value)}
                    disabled={loading || viewMode}
                  />
                </div>
              </div>

              {/* Employee Name */}
              {isEdit ? (
                <div>
                  <label className={labelBase}>
                    Employee Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    className={inputBase}
                    value={employeeNameText}
                    readOnly
                  />
                </div>
              ) : (
                <div className={disabledMask}>
                  <label className={labelBase}>
                    Employee Name <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={inputBase}
                    value={employeeNameId}
                    onChange={(e) => onEmployeeNameChange(e.target.value)}
                    disabled={loading || viewMode}
                  >
                    <option value="Select">Select</option>
                    {(employeeNames || []).map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.text}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Designation */}
              <div>
                <label className={labelBase}>
                  Designation <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <Briefcase
                    className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`}
                  />
                  <input className={inputBase} value={designation} readOnly />
                </div>
              </div>

              {/* From Site */}
              <div className={disabledMask}>
                <label className={labelBase}>
                  From Site <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <MapPin
                    className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`}
                  />
                  <select
                    className={inputBase}
                    value={fromSiteId}
                    onChange={(e) => setFromSiteId(e.target.value)}
                    disabled={true} // ASP sets login branch, doesn't allow change usually
                  >
                    <option value="Select">Select</option>
                    {(fromSites || []).map((b, idx) => (
                      <option
                        key={b.fld_branchid ?? idx}
                        value={b.fld_branchid}
                      >
                        {b.fld_branchname}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  className={`text-xs mt-2 ${isDark ? "text-gray-300" : "text-gray-500"}`}
                >
                  From Site is locked to your login branch.
                </div>
              </div>

              {/* Department */}
              <div>
                <label className={labelBase}>
                  Department <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <Briefcase
                    className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`}
                  />
                  <input className={inputBase} value={department} readOnly />
                </div>
              </div>

              {/* To Site */}
              <div className={disabledMask}>
                <label className={labelBase}>
                  To Site <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <MapPin
                    className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`}
                  />
                  <select
                    className={inputBase}
                    value={toSiteId}
                    onChange={(e) => setToSiteId(e.target.value)}
                    disabled={loading || viewMode}
                  >
                    <option value="Select">Select</option>
                    {(toSites || []).map((b, idx) => (
                      <option
                        key={b.fld_branchid ?? idx}
                        value={b.fld_branchid}
                      >
                        {b.fld_branchname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Daily Bata */}
              <div>
                <label className={labelBase}>
                  Daily Bata <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <Wallet
                    className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`}
                  />
                  <input className={inputBase} value={dailyBata} readOnly />
                </div>
              </div>

              {/* Monthly Salary */}
              <div className={disabledMask}>
                <label className={labelBase}>
                  Monthly Salary <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <IndianRupee
                    className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`}
                  />
                  <input
                    className={inputBase}
                    value={monthlySalary}
                    onChange={(e) => setMonthlySalary(e.target.value)}
                    disabled={loading || viewMode}
                    placeholder="e.g. 25000"
                  />
                </div>
              </div>

              {/* Travelling Advance */}
              <div className={disabledMask}>
                <label className={labelBase}>Travelling Advance</label>
                <div className="mt-2 flex items-center gap-2">
                  <IndianRupee
                    className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`}
                  />
                  <input
                    className={inputBase}
                    value={travelAdvance}
                    onChange={(e) => setTravelAdvance(e.target.value)}
                    disabled={loading || viewMode}
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Canteen Due */}
              <div className={disabledMask}>
                <label className={labelBase}>Canteen Due</label>
                <div className="mt-2 flex items-center gap-2">
                  <IndianRupee
                    className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`}
                  />
                  <input
                    className={inputBase}
                    value={canteenDue}
                    onChange={(e) => setCanteenDue(e.target.value)}
                    disabled={loading || viewMode}
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Bata Paid Upto */}
              <div className={disabledMask}>
                <label className={labelBase}>Bata Paid Upto</label>
                <div className="mt-2 flex items-center gap-2">
                  <CalendarDays
                    className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`}
                  />
                  <input
                    type="date"
                    className={inputBase}
                    value={paidUptoISO}
                    onChange={(e) => setPaidUptoISO(e.target.value)}
                    disabled={loading || viewMode}
                  />
                </div>
              </div>

              {/* Debit To */}
              <div className={disabledMask}>
                <label className={labelBase}>Debit To</label>
                <div className="mt-2 flex items-center gap-2">
                  <Type
                    className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`}
                  />
                  <input
                    className={inputBase}
                    value={debitTo}
                    onChange={(e) => setDebitTo(e.target.value)}
                    disabled={loading || viewMode}
                    placeholder="Debit to..."
                  />
                </div>
              </div>

              {/* Reason */}
              <div className={disabledMask}>
                <label className={labelBase}>
                  Reason for Transfer <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <Type
                    className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`}
                  />
                  <input
                    className={inputBase}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    disabled={loading || viewMode}
                    placeholder="Reason..."
                  />
                </div>
              </div>
            </div>

            {/* Worked Period block */}
            <div className="mt-8">
              <div
                className={`text-lg font-extrabold mb-3 ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Worked Period
              </div>

              <div className={`${cardBase} p-5`}>
                <div
                  className={`grid grid-cols-1 md:grid-cols-4 gap-4 ${disabledMask}`}
                >
                  <div>
                    <label className={labelBase}>From Date</label>
                    <div className="mt-2">
                      <input
                        type="date"
                        className={inputBase}
                        value={workedFromISO}
                        onChange={(e) => setWorkedFromISO(e.target.value)}
                        disabled={loading || viewMode}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelBase}>To Date</label>
                    <div className="mt-2">
                      <input
                        type="date"
                        className={inputBase}
                        value={workedToISO}
                        onChange={(e) => setWorkedToISO(e.target.value)}
                        disabled={loading || viewMode}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelBase}>Total Leave Days</label>
                    <div className="mt-2">
                      <input
                        className={inputBase}
                        value={leaveDays}
                        onChange={(e) => setLeaveDays(e.target.value)}
                        disabled={loading || viewMode}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelBase}>Loss of Pay</label>
                    <div className="mt-2">
                      <input
                        className={inputBase}
                        value={lop}
                        onChange={(e) => setLop(e.target.value)}
                        disabled={loading || viewMode}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col md:flex-row items-center justify-end gap-4 mt-8">
              <button
                type="submit"
                className={primaryBtn}
                disabled={loading || viewMode}
              >
                <Save className="w-4 h-4" />
                {isEdit ? "Update" : "Submit"}
              </button>

              <button
                type="button"
                className={ghostBtn}
                onClick={clearForm}
                disabled={loading || viewMode}
              >
                <RefreshCcw className="w-4 h-4" />
                Reset
              </button>

              {isEdit && (
                <button
                  type="button"
                  className={ghostBtn}
                  onClick={() => setViewMode(true)}
                  disabled={loading}
                >
                  <Eye className="w-4 h-4" />
                  View
                </button>
              )}
            </div>

            {/* hints */}
            <div
              className={`text-xs mt-6 text-center ${isDark ? "text-gray-300" : "text-gray-500"}`}
            >
              Tip: For add mode select employee →
              department/designation/bata/salary auto-fills from API.
            </div>
          </form>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeTransferEntry;
